/**
 * Step 1: Terms & Privacy Acceptance
 *
 * Requires users to:
 * 1. Click Terms of Service link (opens modal)
 * 2. Scroll to the bottom of the Terms document
 * 3. Click "I've Read This" button
 * 4. Repeat for Privacy Policy
 * 5. Check the "I agree" checkbox (enabled only after both documents acknowledged)
 * 6. Check GDPR consent checkbox (required, never pre-checked per GDPR Article 7)
 * 7. Optionally check marketing consent checkbox
 *
 * Note: Closing the modal early (X, backdrop, Escape) does NOT count as reviewed.
 * Users must scroll to the bottom and click the acknowledgment button.
 *
 * WP-AUTH-11: Multi-Step Signup Wizard
 */

import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import Button from '@/components/ui/Button';
import { useWizard } from './WizardContext';
import { useWizardAnalytics } from '@/hooks/useWizardAnalytics';
import LegalDocumentModal from '@/components/auth/LegalDocumentModal';
import { TermsOfServiceContent } from '@/components/legal/TermsOfServiceContent';
import { PrivacyPolicyContent } from '@/components/legal/PrivacyPolicyContent';

/**
 * Check icon component
 */
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * External link icon
 */
function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z"
        clipRule="evenodd"
      />
      <path
        fillRule="evenodd"
        d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export interface Step1TermsProps {
  /** Callback when step is complete and user clicks Next */
  onNext: () => void;
}

export default function Step1Terms({ onNext }: Step1TermsProps) {
  const {
    state,
    markTermsReviewed,
    markPrivacyReviewed,
    setTermsAccepted,
    setGdprConsent,
    setMarketingConsent,
  } = useWizard();

  const analytics = useWizardAnalytics();

  // Modal state for scroll-to-bottom legal document modals
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Track step view on mount
  useEffect(() => {
    analytics.trackStepViewed(1, 'Terms');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    termsReviewed,
    privacyReviewed,
    termsAccepted,
    gdprConsent,
    marketingConsent,
  } = state;

  // Both links must be clicked before checkboxes are enabled
  const canEnableCheckbox = termsReviewed && privacyReviewed;

  // Can proceed when terms AND GDPR consent are accepted
  const canProceed = termsAccepted && gdprConsent;

  /**
   * Handle Terms of Service link click - opens modal
   */
  const handleTermsClick = useCallback(() => {
    setShowTermsModal(true);
  }, []);

  /**
   * Handle Terms modal acknowledgment - user scrolled to bottom and clicked button
   */
  const handleTermsAcknowledge = useCallback(() => {
    markTermsReviewed();
    analytics.trackDocumentReviewed('terms');
    setShowTermsModal(false);
  }, [markTermsReviewed, analytics]);

  /**
   * Handle Privacy Policy link click - opens modal
   */
  const handlePrivacyClick = useCallback(() => {
    setShowPrivacyModal(true);
  }, []);

  /**
   * Handle Privacy modal acknowledgment - user scrolled to bottom and clicked button
   */
  const handlePrivacyAcknowledge = useCallback(() => {
    markPrivacyReviewed();
    analytics.trackDocumentReviewed('privacy');
    setShowPrivacyModal(false);
  }, [markPrivacyReviewed, analytics]);

  /**
   * Handle terms acceptance checkbox change
   */
  const handleTermsCheckboxChange = useCallback(
    (checked: boolean) => {
      setTermsAccepted(checked);
    },
    [setTermsAccepted]
  );

  /**
   * Handle GDPR consent checkbox change
   */
  const handleGdprConsentChange = useCallback(
    (checked: boolean) => {
      setGdprConsent(checked);
    },
    [setGdprConsent]
  );

  /**
   * Handle marketing consent checkbox change
   */
  const handleMarketingConsentChange = useCallback(
    (checked: boolean) => {
      setMarketingConsent(checked);
    },
    [setMarketingConsent]
  );

  /**
   * Handle Next button click
   */
  const handleNext = useCallback(() => {
    if (canProceed) {
      analytics.trackStepCompleted(1, 'Terms');
      onNext();
    }
  }, [canProceed, onNext, analytics]);

  return (
    <div id="wizard-content" className="space-y-6">
      {/* Step heading */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          Review & Accept Terms
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Please review our policies by clicking each link below before
          continuing.
        </p>
      </div>

      {/* Document links */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-gray-700">Review our policies:</p>

        <div className="flex flex-wrap gap-4">
          {/* Terms of Service link */}
          <button
            type="button"
            onClick={handleTermsClick}
            className={cn(
              'inline-flex items-center gap-1.5 text-sm font-medium transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded px-1 py-0.5',
              termsReviewed
                ? 'text-green-600'
                : 'text-primary-600 hover:text-primary-500'
            )}
            aria-label={
              termsReviewed
                ? 'Terms of Service - reviewed'
                : 'Terms of Service - click to review'
            }
          >
            {termsReviewed && <CheckIcon className="w-4 h-4" />}
            <span className="underline">Terms of Service</span>
            {!termsReviewed && <ExternalLinkIcon className="w-3.5 h-3.5" />}
          </button>

          {/* Privacy Policy link */}
          <button
            type="button"
            onClick={handlePrivacyClick}
            className={cn(
              'inline-flex items-center gap-1.5 text-sm font-medium transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded px-1 py-0.5',
              privacyReviewed
                ? 'text-green-600'
                : 'text-primary-600 hover:text-primary-500'
            )}
            aria-label={
              privacyReviewed
                ? 'Privacy Policy - reviewed'
                : 'Privacy Policy - click to review'
            }
          >
            {privacyReviewed && <CheckIcon className="w-4 h-4" />}
            <span className="underline">Privacy Policy</span>
            {!privacyReviewed && <ExternalLinkIcon className="w-3.5 h-3.5" />}
          </button>
        </div>

        {/* Progress indicator */}
        {!canEnableCheckbox && (
          <p className="text-xs text-gray-500">
            {!termsReviewed && !privacyReviewed
              ? 'Click both links above to continue'
              : termsReviewed
                ? 'Now click the Privacy Policy link'
                : 'Now click the Terms of Service link'}
          </p>
        )}
      </div>

      {/* Consent checkboxes - WP-AUTH-16: Hidden until both documents are read (progressive disclosure) */}
      {canEnableCheckbox && (
        <div className="space-y-4 pt-2">
          {/* Terms acceptance checkbox */}
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => handleTermsCheckboxChange(e.target.checked)}
              className="mt-1 h-4 w-4 border-gray-300 rounded focus:ring-primary-500 text-primary-600 cursor-pointer"
            />
            <span className="ml-2 text-sm text-gray-700">
              I have read and agree to the Terms of Service and Privacy Policy
            </span>
          </label>

          {/* GDPR consent checkbox - REQUIRED, NEVER pre-checked (per GDPR Article 7) */}
          <div>
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={gdprConsent}
                onChange={(e) => handleGdprConsentChange(e.target.checked)}
                className="mt-1 h-4 w-4 border-gray-300 rounded focus:ring-primary-500 text-primary-600 cursor-pointer"
                aria-describedby="gdpr-checkbox-hint"
              />
              <span className="ml-2 text-sm text-gray-700">
                I consent to the processing of my personal data as described in the
                Privacy Policy{' '}
                <span className="text-red-500" aria-label="required">
                  *
                </span>
              </span>
            </label>
            <p id="gdpr-checkbox-hint" className="ml-6 text-xs text-gray-500">
              Required for account creation
            </p>
          </div>

          {/* Marketing consent checkbox - Optional */}
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => handleMarketingConsentChange(e.target.checked)}
              className="mt-1 h-4 w-4 border-gray-300 rounded focus:ring-primary-500 text-primary-600 cursor-pointer"
            />
            <span className="ml-2 text-sm text-gray-700">
              I would like to receive product updates and tips via email (optional)
            </span>
          </label>
        </div>
      )}

      {/* Navigation */}
      <div className="pt-4">
        <Button
          type="button"
          onClick={handleNext}
          disabled={!canProceed}
          className="w-full"
        >
          Next
        </Button>
      </div>

      {/* Terms of Service Modal */}
      <LegalDocumentModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAcknowledge={handleTermsAcknowledge}
        title="Terms of Service"
        documentType="terms"
      >
        <TermsOfServiceContent />
      </LegalDocumentModal>

      {/* Privacy Policy Modal */}
      <LegalDocumentModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        onAcknowledge={handlePrivacyAcknowledge}
        title="Privacy Policy"
        documentType="privacy"
      >
        <PrivacyPolicyContent />
      </LegalDocumentModal>
    </div>
  );
}
